import { JsonPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { of, Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Award } from '../../../../models/award.school.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolAwardService } from '../../../../services/award.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as awardActions from '../../../../state/actions/award.school.actions';
import { AppState } from '../../../../state/appstate';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { S3DocumentUploadComponent } from '../../../components/uploaders/s3-document-upload/s3-document-upload.component';

@Component({
  selector: 'app-dlg-upsert-award',
  standalone: true,
  imports: [ReactiveFormsModule, JsonPipe, S3DocumentUploadComponent],
  templateUrl: './dlg-upsert-award.component.html',
  styleUrl: './dlg-upsert-award.component.css',
})
export class DlgUpsertAwardComponent implements OnInit, OnDestroy {
  @ViewChild('uploader') uploader!: S3DocumentUploadComponent;
  store: Store<AppState> = inject(Store);
  toastr: ToastrService = inject(ToastrService);
  awardService = inject(SchoolAwardService);
  readonly dialogRef = inject(MatDialogRef<DlgUpsertAwardComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private dialog: MatDialog = inject(MatDialog);
  fb = inject(FormBuilder);

  pageForm: FormGroup = new FormGroup({});

  uuid = uuidv4();

  title: string = 'Add Certificate';
  description: string = 'Enter Certificate Details';
  action: string = 'Create';

  isAssignable: boolean = true;

  loading$: any = of(false);

  user: UserModel | null = null;

  preSignedUrlEndpoint: string = environment.AWS_SCHOOL_AWARDS_SIGNEDURL;
  apiKey: string = environment.AWS_API_KEY;
  metadata: any = {};
  cloudFrontDomain = environment.AWS_USERS_CLOUDFRONT;
  maxFileSize = 10485760; // 10 MB

  exit$: Subject<void> = new Subject<void>();

  constructor() {
    this.pageForm = this.fb.group({
      awardName: new FormControl('', [Validators.required]),
      studentId: new FormControl(this.data.studentId, [Validators.required]),
      studentName: new FormControl(this.data.studentName, [
        Validators.required,
      ]),
      studentPhotoUrl: new FormControl(this.data.studentPhotoUrl),
    });

    this.title = this.data.title;
    this.description = this.data.description;
    this.action = this.data.action;
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit(): void {
    this.store
      .select((store) => store.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.metadata = {
      name: this.uuid,
      groupId: this.data.groupId,
      studentId: this.data.studentId,
    };
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    if (this.pageForm.invalid || !this.uploader.getIsAttached()) return;

    const uuid = this.data.id ?? this.uuid;

    this.loading$ = of(true);

    this.uploader?.setInputParams({
      studentId: this.data.studentId,
      fileName: uuid,
    });
    this.uploader?.setMetadata(this.metadata);

    this.uploader
      .upload()
      .then((result) => {
        if (result?.successful && result.successful[0]?.response?.uploadURL) {
          this.createDatabaseEntry(
            this.uuid,
            result.successful[0].response?.uploadURL,
          );
        } else {
          this.loading$ = of(false);
          this.onShowAlert({
            code: 'ERR_UPLOAD_AWARD',
            message: 'Failed to upload document. Please try again later',
            title: 'Error Uploading Document',
          });
        }
      })
      .catch((error) => {
        this.loading$ = of(false);
        this.onShowAlert({
          code: 'ERR_UPLOAD_AWARD',
          message: 'Failed to upload document. Please try again later',
          title: 'Error Uploading Document',
        });
      });
  }

  async createDatabaseEntry(id: string, url?: string | undefined) {
    if (this.pageForm.invalid) return;

    let data = {
      ...this.pageForm.value,
      id: id,
      groupId: this.data.groupId,
      academicYear: this.data.academicYear,
      createdBy: this.user?.name,
      createdById: this.user?.uid,
      created: Timestamp.now(),
      modified: Timestamp.now(),
    };

    if (url)
      data['url'] = UtilsService.replaceDomain(url, this.cloudFrontDomain);

    try {
      if (this.data.id) {
        this.store.dispatch(
          awardActions.update({
            groupId: this.data.groupId,
            awardId: this.data.id,
            award: data as Award,
          }),
        );
      } else {
        this.store.dispatch(
          awardActions.create({
            groupId: this.data.groupId,
            award: data as Award,
          }),
        );
      }
      this.dialogRef.close();
    } catch (error) {
      console.log(`Error creating award: ${error}`);
      this.loading$ = of(false);
      this.onShowAlert(error as AppExceptionModel);
    }
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title,
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
